import { useEffect, useState } from "react";
import {
  WebPlatform,
  api,
  inviteTokenFromHash,
  resolveFormFactor,
  useConnectionView,
  type FormFactor,
  type FormFactorOverride,
  type Me,
} from "@helm/core";
import { DesktopShell } from "./shell/DesktopShell";
import { MobileShell } from "./shell/MobileShell";
import { LoginScreen } from "./auth/LoginScreen";
import { DeviceGate } from "./auth/DeviceGate";
import { PasskeyOffer } from "./auth/PasskeyOffer";
import { InstallPrompt } from "./pwa/InstallPrompt";
import { UpdateBanner } from "./pwa/UpdateBanner";
import { useServiceWorker } from "./pwa/useServiceWorker";
import { settingsSync, stopTradingStream, tradingStream } from "./session";
import { useSettings } from "@helm/core";
import { marketData } from "./md/marketData";
import { resetRiskSettings } from "./risk/riskSettings";
import { startAccountFeed } from "./viewmodel/account";
import { lazyComponent, preloadLazyChunks } from "./components/lazy";

export const platform = new WebPlatform();

// Opened only from an invitation link: its own chunk, outside the entry budget (audit S-12).
const InviteScreen = lazyComponent(
  () => import("./auth/InviteScreen").then((m) => m.InviteScreen),
  "invitation",
);

/** After sign-in, when the lazy chunks are fetched in the background. */
const PRELOAD_DELAY_MS = 3000;

export function useFormFactor(override: FormFactorOverride): FormFactor {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = (): void => {
      setWidth(window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return resolveFormFactor(width, override);
}

type AuthState =
  | { kind: "loading" }
  | { kind: "anonymous" }
  | { kind: "signed_in"; me: Me }
  | { kind: "error"; message: string };

function useAuth(): [AuthState, () => void] {
  const [auth, setAuth] = useState<AuthState>({ kind: "loading" });
  const refresh = (): void => {
    api
      .me()
      .then((me) => {
        setAuth(me ? { kind: "signed_in", me } : { kind: "anonymous" });
      })
      .catch((e: unknown) => {
        setAuth({ kind: "error", message: e instanceof Error ? e.message : "API unreachable" });
      });
  };
  useEffect(refresh, []);
  return [auth, refresh];
}

/**
 * The signed-in app. Mounted only behind `DeviceGate`, i.e. once this device may be used
 * (SPEC §20), so the trading stream, settings sync and gateway tokens never run unapproved.
 */
function SignedIn({ me }: { me: Me }): React.JSX.Element {
  const override = useSettings((s) => s.settings.formFactor);
  const formFactor = useFormFactor(override);
  const connection = useConnectionView();

  // Hold the trading stream open while signed in (SPEC §1A.3: every device is a view of the
  // server session), and load the synced settings document.
  useEffect(() => {
    tradingStream();
    void settingsSync.load();
    marketData.enableLive();
    // Accounts / P&L / positions from the trading stream; per-account risk settings load on demand.
    const stopFeed = startAccountFeed();
    return () => {
      stopFeed();
      marketData.disableLive();
      stopTradingStream();
      resetRiskSettings();
    };
  }, []);

  // Warm the lazy chunks (chart, settings, …) once the shell is up, so opening them later needs
  // neither the network nor a parse on the critical path (audit S-12).
  useEffect(() => {
    const t = window.setTimeout(() => {
      void preloadLazyChunks();
    }, PRELOAD_DELAY_MS);
    return () => {
      window.clearTimeout(t);
    };
  }, []);

  return formFactor === "mobile" ? (
    <MobileShell me={me} connection={connection} />
  ) : (
    <DesktopShell formFactor={formFactor} me={me} connection={connection} />
  );
}

export function App(): React.JSX.Element {
  const sw = useServiceWorker();
  const [auth] = useAuth();
  // An invitation link (`/#invite=…`, ADR-0051) sets a password first, whoever is signed in.
  const [invite, setInvite] = useState(() => inviteTokenFromHash(location.hash));
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);

  let body: React.JSX.Element;
  switch (auth.kind) {
    case "loading":
      body = <div className="centered muted">Loading…</div>;
      break;
    case "anonymous":
      body = (
        <LoginScreen
          initialEmail={invitedEmail ?? ""}
          initialNotice={
            invitedEmail ? "Password set. Sign in: TradePrizm emails a code to confirm this device." : null
          }
        />
      );
      break;
    case "error":
      body = (
        <div className="centered">
          <div className="banner banner-info">API unreachable: {auth.message}</div>
        </div>
      );
      break;
    case "signed_in":
      body = (
        <DeviceGate>
          <SignedIn me={auth.me} />
          <PasskeyOffer me={auth.me} />
        </DeviceGate>
      );
      break;
  }
  if (invite !== null) {
    body = (
      <InviteScreen
        token={invite}
        onDone={(email) => {
          // Drop the used link from the address bar and history.
          history.replaceState(null, "", location.pathname + location.search);
          setInvite(null);
          setInvitedEmail(email);
        }}
      />
    );
  }

  return (
    <>
      {sw.updateReady && <UpdateBanner onReload={sw.applyUpdate} />}
      {body}
      <InstallPrompt platform={platform} />
    </>
  );
}
